import { Capacitor } from '@capacitor/core';

import { getDeviceId } from './deviceId';

export type ClientDebugLogLevel = 'log' | 'info' | 'warn';

export type ClientDebugLogSink = (
	level: ClientDebugLogLevel,
	message: string,
	details?: Record<string, unknown>,
) => void;

let clientDebugLogSink: ClientDebugLogSink | undefined;
let cachedNativeDeviceId: string | undefined;
let deviceIdPreload: Promise<void> | null = null;

/** Registers a sink (e.g. Sentry.logger) for auth/OAuth/logout debug lines. */
export function setClientDebugLogSink(sink: ClientDebugLogSink | undefined): void {
	clientDebugLogSink = sink;
}

/**
 * Prefetches native device id so early auth logs include it.
 * Safe to call multiple times; no-ops on web.
 */
export async function preloadClientDebugLogContext(): Promise<void> {
	if (!Capacitor.isNativePlatform()) {
		return;
	}

	if (cachedNativeDeviceId !== undefined) {
		return;
	}

	if (!deviceIdPreload) {
		deviceIdPreload = getDeviceId()
			.then((id) => {
				cachedNativeDeviceId = id;
			})
			.catch(() => {
				// Leave unset — logs still work without deviceId.
			})
			.finally(() => {
				deviceIdPreload = null;
			});
	}

	await deviceIdPreload;
}

function ensureNativeDeviceIdPreload(): void {
	if (!Capacitor.isNativePlatform() || cachedNativeDeviceId !== undefined || deviceIdPreload) {
		return;
	}

	void preloadClientDebugLogContext();
}

/** Platform / device fields auto-attached to every {@link clientDebugLog} call. */
export function getClientDebugLogContext(): Record<string, unknown> {
	const context: Record<string, unknown> = {
		platform: Capacitor.getPlatform(),
		isNative: Capacitor.isNativePlatform(),
	};

	if (Capacitor.isNativePlatform() && cachedNativeDeviceId) {
		context.deviceId = cachedNativeDeviceId;
	}

	return context;
}

const SENSITIVE_QUERY_KEYS = new Set([
	'code',
	'state',
	'session_state',
	'id_token',
	'access_token',
	'refresh_token',
]);

/** Redacts OAuth secrets from URL strings before remote logging. */
export function redactSensitiveUrl(url: string): string {
	try {
		const parsed = new URL(url);
		for (const key of SENSITIVE_QUERY_KEYS) {
			if (parsed.searchParams.has(key)) {
				parsed.searchParams.set(key, 'REDACTED');
			}
		}

		return parsed.toString();
	} catch {
		return url.replace(
			/([?&#](?:code|state|session_state|id_token|access_token|refresh_token)=)[^&]*/gi,
			'$1REDACTED',
		);
	}
}

function sanitizeDetails(
	details: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
	if (!details) {
		return undefined;
	}

	const sanitized: Record<string, unknown> = {};

	for (const [key, value] of Object.entries(details)) {
		if (typeof value === 'string' && (key === 'url' || key.endsWith('Url') || value.includes('://'))) {
			sanitized[key] = redactSensitiveUrl(value);
			continue;
		}

		if (value instanceof Error) {
			sanitized[key] = value.message;
			continue;
		}

		if (
			typeof value === 'string'
			|| typeof value === 'number'
			|| typeof value === 'boolean'
			|| value === null
		) {
			sanitized[key] = value;
			continue;
		}

		try {
			sanitized[key] = JSON.parse(JSON.stringify(value)) as unknown;
		} catch {
			sanitized[key] = String(value);
		}
	}

	return sanitized;
}

/**
 * Dual-writes auth/OAuth/logout diagnostics to the console and an optional remote sink.
 * Console keeps local device debugging; the sink (Sentry Logs) enables realtime client visibility.
 * On native, attaches `deviceId` (when resolved) plus `platform` / `isNative` to every call.
 */
export function clientDebugLog(
	level: ClientDebugLogLevel,
	message: string,
	details?: Record<string, unknown>,
): void {
	ensureNativeDeviceIdPreload();

	const merged = {
		...getClientDebugLogContext(),
		...(details ?? {}),
	};
	const sanitized = sanitizeDetails(merged);

	if (sanitized) {
		if (level === 'warn') {
			console.warn(message, sanitized);
		} else if (level === 'log') {
			console.log(message, sanitized);
		} else {
			console.info(message, sanitized);
		}
	} else if (level === 'warn') {
		console.warn(message);
	} else if (level === 'log') {
		console.log(message);
	} else {
		console.info(message);
	}

	clientDebugLogSink?.(level, message, sanitized);
}

/** @internal Resets module state between unit tests. */
export function resetClientDebugLogForTests(): void {
	clientDebugLogSink = undefined;
	cachedNativeDeviceId = undefined;
	deviceIdPreload = null;
}
